import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, Mail, X, Copy, Check, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import ChatBot from "../components/ChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [stats, setStats] = useState({
    current_jobs: 0,
    monthly_jobs: 0,
    total_clinics: 0
  });
  const [content, setContent] = useState({
    banner_text: "Dijital Diş Protez Laboratuvarı",
    banner_subtext: "Kalite ve Hassasiyetin Buluştuğu Yer",
    hero_title: "Gülüş Tasarımında",
    hero_highlight: "Dijital Hassasiyet",
    hero_description: "En son CAD/CAM teknolojisi ve uzman el işçiliği ile diş hekimleri için mükemmel protez çözümleri sunuyoruz.",
    about_title: "Hakkımızda",
    about_text: "Hisar Dental Lab olarak, modern teknoloji ve geleneksel ustalığı bir araya getirerek diş hekimlerine en kaliteli protez çözümlerini sunuyoruz.",
    stats_label_1: "Anlık Yaptığımız İşler",
    stats_label_2: "Bu Ay Yapılan Toplam",
    stats_label_3: "Çalıştığımız Klinikler"
  });
  const [sliderImages, setSliderImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showContactModal, setShowContactModal] = useState(null);
  const [copied, setCopied] = useState(false);

  const contactInfo = {
    phone: "+90 532 394 83 29",
    email: "info@hisardentallab.com"
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Kopyalandı!");
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchStats();
    fetchSliderImages();
    fetchContent();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchSliderImages = async () => {
    try {
      const response = await axios.get(`${API}/slider`);
      setSliderImages(response.data);
    } catch (error) {
      console.error("Error fetching slider:", error);
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching content:", error);
    }
  };

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (sliderImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  }, [sliderImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  }, [sliderImages.length]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Top Banner - İlgi Çekici Yazı */}
      <section className="bg-gradient-to-r from-[#0A192F] via-cyan-600 to-[#0A192F] py-4 md:py-6" data-testid="top-banner">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-white font-bold text-lg md:text-2xl mb-1">
              {content.banner_text}
            </h2>
            <p className="text-cyan-200 text-sm md:text-base">
              {content.banner_subtext}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image Slider Section */}
      {sliderImages.length > 0 && (
        <section className="relative h-[40vh] md:h-[50vh] overflow-hidden" data-testid="slider-section">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <img
                src={sliderImages[currentSlide]?.image_url?.startsWith('/') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${sliderImages[currentSlide]?.image_url}` 
                  : sliderImages[currentSlide]?.image_url}
                alt={`Slide ${currentSlide + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          {sliderImages.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all z-10"
                data-testid="slider-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white p-3 rounded-full transition-all z-10"
                data-testid="slider-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Dots Indicator */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {sliderImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentSlide
                        ? "bg-cyan-400 w-8"
                        : "bg-white/50 hover:bg-white/80"
                    }`}
                    data-testid={`slider-dot-${index}`}
                  />
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* White Section - Stats Preview */}
      <section className="bg-white py-12 md:py-16" data-testid="white-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-3xl md:text-5xl font-bold text-[#0A192F] mb-2">{stats.total_clinics}</div>
              <div className="text-xs md:text-sm text-slate-600">{content.stats_label_3}</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hero Section - Mavi Alan */}
      <section className="relative py-16 md:py-24 overflow-hidden" data-testid="hero-section">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#0d2341] to-[#0A192F]" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-r from-blue-400 to-cyan-300 rounded-full blur-3xl opacity-15" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {content.hero_title}{" "}
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300 bg-clip-text text-transparent">
                {content.hero_highlight}
              </span>
            </h1>
            <p className="text-base md:text-lg text-white/70 mb-8 max-w-2xl mx-auto">
              {content.hero_description}
            </p>
            
            {/* Contact Buttons */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <button
                onClick={() => setShowContactModal('phone')}
                className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-white hover:bg-white/20 transition-colors text-sm"
                data-testid="contact-phone-btn"
              >
                <Phone className="w-4 h-4 text-cyan-400" />
                <span>Numara</span>
              </button>
              <button
                onClick={() => setShowContactModal('email')}
                className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-white hover:bg-white/20 transition-colors text-sm"
                data-testid="contact-email-btn"
              >
                <Mail className="w-4 h-4 text-cyan-400" />
                <span>E-mail</span>
              </button>
            </div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Link
                to="/iletisim#iletisim-formu"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-full px-6 md:px-8 py-3 md:py-4 font-medium shadow-lg shadow-cyan-500/25 transition-all duration-300 hover:shadow-cyan-500/40 hover:scale-105"
                data-testid="hero-cta"
              >
                İletişim Kur
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Section - Hakkımızda (Footer yerine) */}
      <section className="bg-white py-16 md:py-20" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#0A192F] mb-6">
              {content.about_title}
            </h2>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-6">
              {content.about_text}
            </p>
            <p className="text-slate-400 text-sm">
              © 2020 Hisar Dental Diş Protez Laboratuvarı. Tüm hakları saklıdır.
            </p>
          </motion.div>
        </div>
      </section>

      <ChatBot />
      
      {/* Contact Info Modal */}
      <AnimatePresence>
        {showContactModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowContactModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-[#0A192F]">
                  {showContactModal === 'phone' ? 'Telefon Numarası' : 'E-posta Adresi'}
                </h3>
                <button
                  onClick={() => setShowContactModal(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-slate-100 rounded-xl p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  {showContactModal === 'phone' ? (
                    <Phone className="w-5 h-5 text-cyan-500" />
                  ) : (
                    <Mail className="w-5 h-5 text-cyan-500" />
                  )}
                  <span className="font-medium text-[#0A192F] select-all">
                    {showContactModal === 'phone' ? contactInfo.phone : contactInfo.email}
                  </span>
                </div>
                <button
                  onClick={() => handleCopy(showContactModal === 'phone' ? contactInfo.phone : contactInfo.email)}
                  className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  title="Kopyala"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5 text-slate-500" />
                  )}
                </button>
              </div>
              
              <p className="text-sm text-slate-500 mt-3 text-center">
                Kopyalamak için ikona tıklayın veya seçip kopyalayın
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
